import { entities } from "@fraction/shared";
import _ from "lodash";
import { HelpCircleIcon, Loader } from "lucide-react";
import { useCallback } from "react";
import Skeleton from "src/components/Skeleton";
import Tooltip from "src/components/Tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { useApplication } from "src/hooks/useApplication";
import { Logger } from "src/log";
import { cn } from "src/utilities/shadcnUtils";

const logger = new Logger("ModifiableKeyValue");

export const ModifiableKeySelect = ({
  label,
  loading,
  appId,
  path,
  options,
  formatValue,
  mutateValueBeforeUpdate,
  updateOtherApplicationValuesOnUpdate,
  className,
  tooltip,
  defaultValue,
}: {
  label: string;
  loading?: boolean;
  appId?: string;
  path: string;
  options: { value: string; label: string }[];
  formatValue?: (val: any) => string;
  mutateValueBeforeUpdate?: (val: string) => any;
  className?: string;
  tooltip?: React.ReactNode;
  defaultValue?: string;
  updateOtherApplicationValuesOnUpdate?: (val: entities.ApplicationT) => Record<string, any>;
}) => {
  const {
    updateApplication,
    updateApplicationProperty,
    upsertApplicationSolicitor,
    data: application,
    isPending,
  } = useApplication({
    id: appId,
    initialRefetch: false,
    cached: true,
  });

  const value = _.get(application, path);

  const handleValueChange = useCallback(
    (newValue: string) => {
      let newValueToUse: any = newValue;
      if (mutateValueBeforeUpdate) {
        newValueToUse = mutateValueBeforeUpdate(newValue);
      }

      const propertyChanges: Record<string, any> = {};
      const solicitorChanges: Record<string, any> = {};
      const applicationChanges: Record<string, any> = {};

      if (newValueToUse === "") {
        newValueToUse = null;
      }

      if (newValueToUse === value) {
        return;
      }

      if (path.startsWith("property.")) {
        logger.log(`Setting property ${path}: ${newValueToUse}`);
        const withoutPropertyPrefix = path.replace("property.", "");
        propertyChanges[withoutPropertyPrefix] = newValueToUse;
      } else if (path.startsWith("solicitor.")) {
        const withoutPrefix = path.replace("solicitor.", "");
        logger.log(`Setting solicitor ${path} (${withoutPrefix}): ${newValueToUse}`);
        solicitorChanges[withoutPrefix] = newValueToUse;
      } else {
        logger.log(`Setting application ${path}: ${newValueToUse}`);
        applicationChanges[path] = newValueToUse;
      }

      if (updateOtherApplicationValuesOnUpdate) {
        const otherApplicationChanges = updateOtherApplicationValuesOnUpdate({
          ...application,
          ...applicationChanges,
        });

        if (otherApplicationChanges) {
          Object.assign(applicationChanges, otherApplicationChanges);
        }
      }

      if (Object.keys(propertyChanges).length) {
        updateApplicationProperty(propertyChanges);
      }
      if (Object.keys(solicitorChanges).length) {
        upsertApplicationSolicitor.mutateAsync(solicitorChanges);
      }
      if (Object.keys(applicationChanges).length) {
        updateApplication(applicationChanges);
      }
    },
    [value, path, mutateValueBeforeUpdate, updateOtherApplicationValuesOnUpdate]
  );

  let fmtValue = value;
  if (formatValue && value) {
    fmtValue = formatValue(value);
  }

  return (
    <div className={cn("flex flex-row flex-wrap gap-1 items-center justify-between p-3 rounded", className)}>
      <div className="flex flex-row mr-2">
        <p className="text-sm font-bold flex-1">{label}:</p>
        {tooltip ? (
          <Tooltip white text={tooltip}>
            <HelpCircleIcon className="ml-1" size={12} />
          </Tooltip>
        ) : null}
      </div>

      {loading ? (
        <Skeleton width={160} />
      ) : (
        <div className="flex flex-row max-w-[300px]">
          <div className="relative flex flex-row">
            <Select defaultValue={defaultValue} value={fmtValue} onValueChange={handleValueChange}>
              <SelectTrigger
                className={cn(
                  "text-sm text-right text-ellipsis line-clamp-2 bg-gray-400 py-1 pr-2 pl-3 rounded-md min-w-[120px] min-w-auto"
                )}
                rightIcon={isPending ? <Loader className="w-4 text-gray-600 animate-spin" /> : undefined}
              >
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};
